/**
 * @format
 * @module lib/identity/entitlements
 * @see https://confluence.theglobeandmail.com/display/TeamSubs/Government+IPring+-+SSVCS+Values
 * @see https://confluence.theglobeandmail.com/display/QA/How+to+look+up+SSVCS+Values
 * @see https://confluence.theglobeandmail.com/display/AT/SSVC+Value+structure
 */
import isNil from "lodash/isNil";
import tgamConfig from "config/tgam";
import { getProp } from "utils/utils";

/**
 * Possible entitlement types of a TGAM user
 * @property {{ value: String, className: String }} ADVISOR
 * @property {{ value: String }} ONDEMAND - PREMIUM or ENTERPRISE
 * @property {{ value: String }} GLOBEPRINT - The Globe print
 * @property {{ value: String }} NYTPRINT - New York Times print
 * @property {{ value: String }} FINTOOLSTRIAL - Financial Tools Trial
 */
export const ENTITLEMENTS = {
  ADVISOR: {
    value: "GA~GA~~~",
    className: "advisor"
  },
  ONDEMAND: {
    value: "PREMIUM~ONDEMAND~~"
  },
  GLOBEPRINT: {
    value: "NPGAM~NPGAM~~"
  },
  NYTPRINT: {
    value: "NYT~NYT~~"
  },
  FINTOOLSTRIAL: {
    value: "FINANCE~FINTRIAL~~"
  }
};

/**
 * Possible premium on-demand subtypes of a TGAM user
 * @property {String} ENTERPRISE - enterprise access
 * @property {String} IPRING - IP-Ring for the Feds
 */
export const DEMANDS = {
  ENTERPRISE: "enterprise"
  // IPRING: "ipring" // currently unused
};

/**
 * Check if the entitlement key is present, and return true when it is.
 * @param {Object} entitlement - The ENTITLEMENT object to check
 * @returns {Boolean} true if present
 */
export function hasEntitlement(entitlement) {
  return getProp(tgamConfig, "datalayer.identity.ssvcs", "").includes(entitlement.value);
}

/**
 * Check if the on-demand value is present, and return true when it is.
 * @param {String} demand - The DEMAND string to compare
 * @returns {Boolean} true if true
 */
export function hasDemand(demand) {
  return getProp(tgamConfig, "datalayer.identity.demand", "") === demand;
}

/**
 * Get all entitlement's for current user, as an array of class names.
 * @returns {Array<String>} of class names
 */
export function getEntitlementClasses() {
  return Object.keys(ENTITLEMENTS)
    .map(entitlement => {
      let e = ENTITLEMENTS[entitlement];
      if (hasEntitlement(e)) {
        return e.className || null;
      }
    })
    .filter(e => !isNil(e));
}

/**
 * Add a specific entitlement from a SSVCS string, for a user.
 * @param {String} [serviceString=""] - to be processed
 * @param {String} [entitlement=""] - to add
 * @returns {String} of updated serviceString
 */
export function addEntitlement(serviceString = "", entitlement = "") {
  if (serviceString.includes(entitlement)) {
    return serviceString;
  }
  let services = serviceString.length ? serviceString.split(",") : [];
  services.push(entitlement);
  return services.join(",");
}

/**
 * Remove a specific entitlement from a SSVCS string, for a user.
 * @param {String} [serviceString=""] - to be processed
 * @param {String} [entitlement=""] - to remove
 * @returns {String} of updated serviceString
 */
export function removeEntitlement(serviceString = "", entitlement = "") {
  return serviceString
    .split(",")
    .filter(value => value !== entitlement)
    .join(",");
}
